import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL, APP_NAME, OLYMP_API_BASE_URL } from '../config/api';
import { useAuth } from '../auth/authContext';
import { useLocation } from 'react-router-dom';
import { 
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Switch,
  TextField,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Tabs,
  Tab,
  FormControlLabel,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Slider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { 
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminPanelSettingsIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage, SUPPORTED_LANGUAGES } from '../i18n/languages';
import { useAppearance } from '../context/AppearanceContext';

const userProfile = {
  name: '',
  email: '',
  company: '',
  role: '',
  phone: '',
  address: '',
  avatar: null,
  department: '',
  language: 'en',
  theme: 'light',
  twoFactorEnabled: false,
};

const billingInfo = {
  plan: 'Institutional',
  nextBilling: '-',
  paymentMethod: '-',
  accountNumber: '-',
  invoices: [],
};

const organizationSettings = {
  name: '',
  type: '',
  size: '',
  industry: '',
  website: '',
  logo: null,
  primaryColor: '#FF7A00',
  id: null,
  members: [],
  departments: [],
  teams: [],
};

const notificationSettings = {
  email: {
    newRequests: true,
    statusChanges: true,
    messages: true,
    billing: true,
    weeklyReport: false,
    paymentReceived: true
  },
  inApp: {
    newRequests: true,
    statusChanges: true,
    messages: true,
    billing: true,
    paymentReceived: true
  }
};

const privacySettings = {
  dataSharing: 'minimal',
  anonymizeResults: true,
  allowAnalytics: true,
  retentionPeriod: 36, // Monate
  dataEncryption: 'advanced'
};

const apiSettings = {
  enabled: true,
  rateLimit: 100,
  webhookUrl: '',
  webhookEvents: {
    analysisResults: true,
    statusChanges: true,
    messages: false,
  },
  ipWhitelist: [],
  dataExportSettings: {
    allowedFormats: ['csv', 'json', 'xlsx'],
    encryptionRequired: true,
  },
  apiKey: '',
};

const appearanceSettings = {
  theme: 'light',
  primaryColor: '#FF7A00',
  language: 'de',
  fontScale: 1,
  animationsEnabled: true,
  dateFormat: 'dd.mm.yyyy',
  timezone: 'Europe/Berlin',
  defaultHomepage: 'dashboard',
  dashboardWidgets: {
    incomingRequests: true,
    statistics: true,
    runningAnalyses: true,
    paymentOverview: true,
    activityLog: false
  }
};

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [showApiKey, setShowApiKey] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(userProfile);
  const [currentBilling, setCurrentBilling] = useState(billingInfo);
  const [currentOrg, setCurrentOrg] = useState(organizationSettings);
  const [currentNotifications, setCurrentNotifications] = useState(notificationSettings);
  const [currentPrivacy, setCurrentPrivacy] = useState(privacySettings);
  const [currentApi, setCurrentApi] = useState(apiSettings);
  const [currentAppearance, setCurrentAppearance] = useState(appearanceSettings);
  const [memberDraft, setMemberDraft] = useState({ email: '', role: 'member' });
  const [editMemberDialog, setEditMemberDialog] = useState({ open: false, member: null, role: 'member' });
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState({ open: false, memberId: null, memberName: '' });
  const [passwordFields, setPasswordFields] = useState({
    oldPassword: '',
    newPassword: '',
    newPasswordConfirm: ''
  });
  const [newIpAddress, setNewIpAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState(null);
  const [twoFaDialogOpen, setTwoFaDialogOpen] = useState(false);
  const [twoFaQrCode, setTwoFaQrCode] = useState('');
  const [twoFaSecret, setTwoFaSecret] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaDisableDialogOpen, setTwoFaDisableDialogOpen] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsLoaded, setActivityLogsLoaded] = useState(false);
  const [deptDialog, setDeptDialog] = useState({ open: false, name: '', description: '', loading: false });
  const [teamDialog, setTeamDialog] = useState({ open: false, name: '', description: '', departmentId: '', loading: false });
  const [deleteItemDialog, setDeleteItemDialog] = useState({ open: false, type: '', id: null, name: '' });
  const avatarInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const { user } = useAuth();
  const { refreshAppearance, updateAppearance } = useAppearance();
  const normalizedLanguage = normalizeLanguage(
    i18n.resolvedLanguage || i18n.language || currentProfile.language || 'en',
  );

  // Calculate tab indices based on whether billing tab is shown
  const showBillingTab = APP_NAME !== 'OLYMP';
  const TAB_INDICES = {
    PROFILE: 0,
    ORGANIZATION: 1,
    NOTIFICATIONS: 2,
    BILLING: showBillingTab ? 3 : -1,
    API_INTEGRATIONS: showBillingTab ? 4 : 3,
    APPEARANCE: showBillingTab ? 5 : 4,
    ADMIN: showBillingTab ? 6 : 5,
  };

  const currentUserOrgRole = currentOrg.members.find(
    (m) => m.email === currentProfile.email
  )?.role_in_organization || 'member';
  const isOrgAdmin = currentUserOrgRole === 'admin';

  const organizationTypeKeyMap = {
    hospital: 'hospital',
    clinic: 'clinic',
    research: 'research',
    university: 'university',
    pharma: 'pharma',
    biotech: 'biotech',
    other: 'other',
    Krankenhaus: 'hospital',
    Klinik: 'clinic',
    Forschungseinrichtung: 'research',
    Universität: 'university',
    Pharmaunternehmen: 'pharma',
    'Biotech-Unternehmen': 'biotech',
    Sonstiges: 'other',
  };

  const organizationSizeKeyMap = {
    '1-50': '1-50',
    '51-200': '51-200',
    '201-1000': '201-1000',
    '1001-5000': '1001-5000',
    '5000+': '5000+',
    '1-50 Mitarbeiter': '1-50',
    '51-200 Mitarbeiter': '51-200',
    '201-1000 Mitarbeiter': '201-1000',
    '1001-5000 Mitarbeiter': '1001-5000',
    '5000+ Mitarbeiter': '5000+',
  };

  const memberRoleKeyMap = {
    admin: 'admin',
    Admin: 'admin',
    viewer: 'viewer',
    Viewer: 'viewer',
    member: 'member',
    Member: 'member',
    dept_admin: 'dept_admin',
    team_admin: 'team_admin',
    data_steward: 'data_steward',
    guest: 'guest',
  };

  const organizationTypeOptions = ['hospital', 'clinic', 'research', 'university', 'pharma', 'biotech', 'other'];
  const organizationSizeOptions = ['1-50', '51-200', '201-1000', '1001-5000', '5000+'];
  const memberRoleOptions = ['admin', 'dept_admin', 'team_admin', 'member', 'data_steward', 'guest'];
  const dataSharingOptions = ['minimal', 'standard', 'extended'];
  const dashboardHomepageOptions = ['dashboard', 'requests', 'negotiations', 'executions'];
  const timeZoneOptions = [
    { value: 'Europe/Berlin', key: 'berlin' },
    { value: 'Europe/London', key: 'london' },
    { value: 'America/New_York', key: 'newYork' },
    { value: 'Asia/Tokyo', key: 'tokyo' },
  ];
  const fontScaleMarks = [
    { value: 0.8, label: t('settings.sections.appearance.fontScale.small') },
    { value: 1, label: t('settings.sections.appearance.fontScale.normal') },
    { value: 1.2, label: t('settings.sections.appearance.fontScale.large') },
  ];
  const retentionMarks = [
    { value: 6, label: t('settings.sections.privacy.retentionMarks.months6') },
    { value: 12, label: t('settings.sections.privacy.retentionMarks.year1') },
    { value: 24, label: t('settings.sections.privacy.retentionMarks.years2') },
    { value: 36, label: t('settings.sections.privacy.retentionMarks.years3') },
    { value: 60, label: t('settings.sections.privacy.retentionMarks.years5') },
  ];

  const getOrganizationTypeLabel = (value) => {
    const mapped = organizationTypeKeyMap[value] || value;
    return t(`settings.sections.organization.types.${mapped}`, value);
  };

  const getOrganizationSizeLabel = (value) => {
    const mapped = organizationSizeKeyMap[value] || value;
    return t(`settings.sections.organization.sizes.${mapped}`, value);
  };

  const getMemberRoleLabel = (value) => {
    const mapped = memberRoleKeyMap[value] || value;
    return t(`settings.sections.organization.roles.${mapped}`, value);
  };

  const getDashboardHomepageLabel = (value) =>
    t(`settings.sections.appearance.homepageOptions.${value}`, value);

  const getInvoiceStatusLabel = (value) =>
    value === 'bezahlt' || value === 'paid'
      ? t('settings.sections.billing.paid')
      : value;

  const formatRetentionValue = (value) => {
    if (value === 12) return t('settings.sections.privacy.retentionMarks.year1');
    if (value < 12) return `${value} ${value === 1 ? t('settings.time.month') : t('settings.time.months')}`;
    const years = value / 12;
    return `${years} ${years === 1 ? t('settings.time.year') : t('settings.time.years')}`;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getAuthHeaders = (token) => ({
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const AUTH_SERVICE_LANGUAGES = new Set(['de', 'en', 'fr', 'es']);
  const toAuthLang = (lang) => {
    if (!lang) return 'en';
    const base = lang.split('-')[0].toLowerCase();
    return AUTH_SERVICE_LANGUAGES.has(base) ? base : 'en';
  };

  const buildProfilePayload = (overrides = {}) => ({
    first_name: currentProfile.name.split(' ')[0] || '',
    last_name: currentProfile.name.split(' ').slice(1).join(' '),
    phone: currentProfile.phone,
    company: currentProfile.company,
    department: currentProfile.department,
    position: currentProfile.role,
    address: currentProfile.address,
    language: toAuthLang(currentProfile.language),
    timezone: currentAppearance.timezone,
    ...overrides,
  });

  const loadOrganizationData = async (token) => {
    try {
      const orgResponse = await axios.get(
        `${API_BASE_URL}/organizations/my-organization/`,
        getAuthHeaders(token),
      );

      const orgId = orgResponse.data.id;
      setCurrentOrg((prev) => ({
        ...prev,
        id: orgId,
        name: orgResponse.data.name || '',
        type: orgResponse.data.type || '',
        size: orgResponse.data.size || '',
        industry: orgResponse.data.industry || '',
        website: orgResponse.data.website || '',
        primaryColor: orgResponse.data.primary_color || prev.primaryColor || '#0066CC',
        members: orgResponse.data.members || [],
      }));
      await loadDepartmentsAndTeams(token, orgId);
    } catch (orgError) {
      console.log('Organization missing or failed to load:', orgError.response?.status);
    }
  };

  const loadDepartmentsAndTeams = async (token, orgId) => {
    if (!orgId) return;
    try {
      const [deptsRes, teamsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/organizations/${orgId}/departments/`, getAuthHeaders(token)),
        axios.get(`${API_BASE_URL}/organizations/${orgId}/teams/`, getAuthHeaders(token)),
      ]);
      setCurrentOrg((prev) => ({
        ...prev,
        departments: deptsRes.data || [],
        teams: teamsRes.data || [],
      }));
    } catch (err) {
      console.warn('Failed to load departments/teams:', err.message);
    }
  };

  const handleCreateDepartment = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !currentOrg.id) return;
    setDeptDialog((d) => ({ ...d, loading: true }));
    try {
      const res = await axios.post(
        `${API_BASE_URL}/organizations/${currentOrg.id}/departments/`,
        { name: deptDialog.name, description: deptDialog.description },
        getAuthHeaders(token),
      );
      setCurrentOrg((prev) => ({ ...prev, departments: [...prev.departments, res.data] }));
      setDeptDialog({ open: false, name: '', description: '', loading: false });
      showSnackbar('Department created.');
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Failed to create department.', 'error');
      setDeptDialog((d) => ({ ...d, loading: false }));
    }
  };

  const handleCreateTeam = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !currentOrg.id) return;
    setTeamDialog((d) => ({ ...d, loading: true }));
    try {
      const payload = { name: teamDialog.name, description: teamDialog.description };
      if (teamDialog.departmentId) payload.department = teamDialog.departmentId;
      const res = await axios.post(
        `${API_BASE_URL}/organizations/${currentOrg.id}/teams/`,
        payload,
        getAuthHeaders(token),
      );
      setCurrentOrg((prev) => ({ ...prev, teams: [...prev.teams, res.data] }));
      setTeamDialog({ open: false, name: '', description: '', departmentId: '', loading: false });
      showSnackbar('Team created.');
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Failed to create team.', 'error');
      setTeamDialog((d) => ({ ...d, loading: false }));
    }
  };

  const handleDeleteDeptOrTeam = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !currentOrg.id || !deleteItemDialog.id) return;
    const { type, id } = deleteItemDialog;
    const path = type === 'department'
      ? `${API_BASE_URL}/organizations/${currentOrg.id}/departments/${id}/`
      : `${API_BASE_URL}/organizations/${currentOrg.id}/teams/${id}/`;
    try {
      await axios.delete(path, getAuthHeaders(token));
      if (type === 'department') {
        setCurrentOrg((prev) => ({ ...prev, departments: prev.departments.filter((d) => d.id !== id) }));
      } else {
        setCurrentOrg((prev) => ({ ...prev, teams: prev.teams.filter((t) => t.id !== id) }));
      }
      setDeleteItemDialog({ open: false, type: '', id: null, name: '' });
      showSnackbar(`${type === 'department' ? 'Department' : 'Team'} deleted.`);
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Delete failed.', 'error');
    }
  };

  const loadOlympSettingsData = async (token, profileData = null, organizationData = null) => {
    const settingsResponse = await axios.get(
      `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
      getAuthHeaders(token),
    );
    const remoteSettings = settingsResponse.data || {};

    setCurrentNotifications({
      ...notificationSettings,
      ...(remoteSettings.notificationSettings || {}),
    });
    setCurrentPrivacy({
      ...privacySettings,
      ...(remoteSettings.privacySettings || {}),
    });
    setCurrentApi({
      ...apiSettings,
      ...(remoteSettings.apiSettings || {}),
      webhookEvents: {
        ...apiSettings.webhookEvents,
        ...(remoteSettings.apiSettings?.webhookEvents || {}),
      },
      dataExportSettings: {
        ...apiSettings.dataExportSettings,
        ...(remoteSettings.apiSettings?.dataExportSettings || {}),
      },
      ipWhitelist: remoteSettings.apiSettings?.ipWhitelist || apiSettings.ipWhitelist,
      apiKey: remoteSettings.apiSettings?.apiKey || apiSettings.apiKey,
    });

    const resolvedAppearance = {
      ...appearanceSettings,
      ...(remoteSettings.appearanceSettings || {}),
      primaryColor:
        remoteSettings.appearanceSettings?.primaryColor
        || organizationData?.primary_color
        || appearanceSettings.primaryColor,
      timezone:
        remoteSettings.appearanceSettings?.timezone
        || profileData?.timezone
        || appearanceSettings.timezone,
      language:
        normalizeLanguage(remoteSettings.appearanceSettings?.language)
        || normalizeLanguage(profileData?.language)
        || appearanceSettings.language,
    };
    setCurrentAppearance(resolvedAppearance);

    setCurrentProfile((prev) => ({
      ...prev,
      language:
        normalizeLanguage(profileData?.language)
        || normalizeLanguage(remoteSettings.appearanceSettings?.language)
        || prev.language,
      twoFactorEnabled:
        remoteSettings.securitySettings?.twoFactorEnabled ?? prev.twoFactorEnabled,
      theme: resolvedAppearance.theme,
    }));
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.log('No auth token found');
          setAuthStatus({ connected: false, error: t('settings.authErrors.notSignedIn') });
          setLoading(false);
          return;
        }

        const profileResponse = await axios.get(
          `${API_BASE_URL}/users/me/`,
          getAuthHeaders(token),
        );

        console.log('Profile data loaded:', profileResponse.data);

        setCurrentProfile({
          name: profileResponse.data.name || `${profileResponse.data.first_name} ${profileResponse.data.last_name}`,
          email: profileResponse.data.email,
          company: profileResponse.data.company || '',
          role: profileResponse.data.position || '',
          phone: profileResponse.data.phone || '',
          address: profileResponse.data.address || '',
          avatar: null,
          department: profileResponse.data.department || '',
          language: profileResponse.data.language || 'de',
          theme: appearanceSettings.theme,
          twoFactorEnabled: currentProfile.twoFactorEnabled
        });

        let organizationData = null;
        try {
          const orgResponse = await axios.get(
            `${API_BASE_URL}/organizations/my-organization/`,
            getAuthHeaders(token),
          );
          organizationData = orgResponse.data;
          setCurrentOrg((prev) => ({
            ...prev,
            id: orgResponse.data.id,
            name: orgResponse.data.name || '',
            type: orgResponse.data.type || '',
            size: orgResponse.data.size || '',
            industry: orgResponse.data.industry || '',
            website: orgResponse.data.website || '',
            primaryColor: orgResponse.data.primary_color || prev.primaryColor || '#0066CC',
            members: orgResponse.data.members || [],
          }));
        } catch (orgError) {
          console.log('Organization missing or failed to load:', orgError.response?.status);
        }

        try {
          await loadOlympSettingsData(token, profileResponse.data, organizationData);
        } catch (olympError) {
          console.warn('Olymp settings unavailable (non-critical):', olympError.message);
        }

        setAuthStatus({ connected: true, message: t('settings.authStatus.connected') });
        setLoading(false);
      } catch (error) {
        console.error('Error while loading settings data:', error);
        setAuthStatus({
          connected: false,
          error: error.response?.status === 401 ? t('settings.authErrors.unauthenticated') : t('settings.authErrors.connectionFailed')
        });
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    setCurrentProfile((prev) => {
      if (prev.language === normalizedLanguage) return prev;
      return {
        ...prev,
        language: normalizedLanguage,
      };
    });
  }, [normalizedLanguage]);

  useEffect(() => {
    if (activeTab === TAB_INDICES.ADMIN && !activityLogsLoaded && !activityLogsLoading) {
      loadActivityLogs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (location.state?.openTab !== undefined) {
      setActiveTab(location.state.openTab);
    }
  }, [location.state]);

  const handleSaveSettings = async (section) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showSnackbar(t('settings.snackbars.authRequired'), 'error');
        return;
      }

      if (section === 'profile') {
        const response = await axios.put(
          `${API_BASE_URL}/users/me/`,
          buildProfilePayload(),
          getAuthHeaders(token),
        );
        await axios.patch(
          `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
          {
            securitySettings: {
              twoFactorEnabled: currentProfile.twoFactorEnabled,
            },
          },
          getAuthHeaders(token),
        );
        console.log('Profile saved:', response.data);
        showSnackbar(t('settings.snackbars.profileSaved'));
      } else if (section === 'organization') {
        if (!currentOrg.name.trim()) {
          showSnackbar(t('settings.snackbars.orgNameRequired'), 'error');
          return;
        }
        const rawWebsite = currentOrg.website?.trim();
        const normalizedWebsite = rawWebsite && !/^https?:\/\//i.test(rawWebsite)
          ? `https://${rawWebsite}`
          : rawWebsite;
        const orgPayload = {
          name: currentOrg.name,
          type: currentOrg.type,
          size: currentOrg.size,
          industry: currentOrg.industry,
          website: normalizedWebsite,
          primary_color: currentOrg.primaryColor,
        };
        if (!currentOrg.id) {
          await axios.post(
            `${API_BASE_URL}/organizations/`,
            orgPayload,
            getAuthHeaders(token),
          );
          await loadOrganizationData(token);
          showSnackbar(t('settings.snackbars.organizationCreated'));
        } else {
          await axios.put(
            `${API_BASE_URL}/organizations/${currentOrg.id}/`,
            orgPayload,
            getAuthHeaders(token),
          );
          showSnackbar(t('settings.snackbars.organizationSaved'));
        }
      } else if (section === 'notifications') {
        await axios.patch(
          `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
          { notificationSettings: currentNotifications },
          getAuthHeaders(token),
        );
        showSnackbar(t('settings.snackbars.notificationsSaved'));
      } else if (section === 'privacy') {
        const passwordTouched = Object.values(passwordFields).some((value) => value.trim() !== '');
        if (passwordTouched) {
          if (!passwordFields.oldPassword) {
            showSnackbar(t('settings.snackbars.oldPasswordRequired'), 'error');
            return;
          }
          if (passwordFields.newPassword.length < 8) {
            showSnackbar(t('settings.snackbars.passwordTooShort'), 'error');
            return;
          }
          if (passwordFields.newPassword !== passwordFields.newPasswordConfirm) {
            showSnackbar(t('settings.snackbars.passwordMismatch'), 'error');
            return;
          }
          await axios.post(
            `${API_BASE_URL}/users/change-password/`,
            {
              old_password: passwordFields.oldPassword,
              new_password: passwordFields.newPassword,
              new_password_confirm: passwordFields.newPasswordConfirm,
            },
            getAuthHeaders(token),
          );
          setPasswordFields({
            oldPassword: '',
            newPassword: '',
            newPasswordConfirm: '',
          });
        }

        await axios.patch(
          `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
          {
            privacySettings: currentPrivacy,
            securitySettings: {
              twoFactorEnabled: currentProfile.twoFactorEnabled,
            },
          },
          getAuthHeaders(token),
        );
        showSnackbar(t('settings.snackbars.privacySaved'));
      } else if (section === 'webhook' || section === 'api') {
        await axios.patch(
          `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
          { apiSettings: currentApi },
          getAuthHeaders(token),
        );
        showSnackbar(t('settings.snackbars.apiSaved'));
      } else if (section === 'appearance') {
        const normalizedAppearanceLanguage = normalizeLanguage(
          currentProfile.language || currentAppearance.language || normalizedLanguage,
        );

        await axios.put(
          `${API_BASE_URL}/users/me/`,
          buildProfilePayload({
            language: toAuthLang(normalizedAppearanceLanguage),
            timezone: currentAppearance.timezone,
          }),
          getAuthHeaders(token),
        );

        if (currentOrg.id) {
          const rawOrgWebsite = currentOrg.website?.trim();
          const normalizedOrgWebsite = rawOrgWebsite && !/^https?:\/\//i.test(rawOrgWebsite)
            ? `https://${rawOrgWebsite}`
            : rawOrgWebsite;
          await axios.put(
            `${API_BASE_URL}/organizations/${currentOrg.id}/`,
            {
              name: currentOrg.name,
              type: currentOrg.type,
              size: currentOrg.size,
              industry: currentOrg.industry,
              website: normalizedOrgWebsite,
              primary_color: currentAppearance.primaryColor,
            },
            getAuthHeaders(token),
          );
          setCurrentOrg((prev) => ({
            ...prev,
            primaryColor: currentAppearance.primaryColor,
          }));
        }

        await axios.patch(
          `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
          {
            appearanceSettings: {
              ...currentAppearance,
              language: normalizedAppearanceLanguage,
            },
          },
          getAuthHeaders(token),
        );
        setCurrentAppearance((prev) => ({
          ...prev,
          language: normalizedAppearanceLanguage,
        }));
        setCurrentProfile((prev) => ({
          ...prev,
          language: normalizedAppearanceLanguage,
        }));
        showSnackbar(t('settings.snackbars.appearanceSaved'));
        refreshAppearance();
      } else {
        showSnackbar(t('settings.snackbars.genericSaved', { section }));
      }
    } catch (error) {
      console.error(`Error while saving ${section} settings:`, error);
      const errData = error.response?.data;
      const errorMessage =
        errData?.detail
        || errData?.error?.message
        || errData?.error?.fields?.old_password?.[0]
        || errData?.error?.fields?.new_password?.[0]
        || errData?.old_password?.[0]
        || errData?.new_password_confirm?.[0]
        || error.message;
      showSnackbar(t('settings.snackbars.saveError', { message: errorMessage }), 'error');
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  const handleProfileChange = (field, value) => {
    setCurrentProfile({
      ...currentProfile,
      [field]: value
    });
  };

  const handleLanguageChange = (value) => {
    const nextLanguage = normalizeLanguage(value);
    handleProfileChange('language', nextLanguage);
    i18n.changeLanguage(nextLanguage);
  };
  
  const handleNotificationChange = (category, setting, value) => {
    setCurrentNotifications({
      ...currentNotifications,
      [category]: {
        ...currentNotifications[category],
        [setting]: value
      }
    });
  };
  
  const handlePrivacyChange = (field, value) => {
    setCurrentPrivacy({
      ...currentPrivacy,
      [field]: value
    });
  };
  
  const handleApiChange = (field, value) => {
    setCurrentApi({
      ...currentApi,
      [field]: value
    });
  };

  const handleApiWebhookEventChange = (field, value) => {
    setCurrentApi((prev) => ({
      ...prev,
      webhookEvents: {
        ...prev.webhookEvents,
        [field]: value,
      },
    }));
  };

  const handleAppearanceChange = (field, value) => {
    setCurrentAppearance((prev) => ({
      ...prev,
      [field]: value,
    }));
    updateAppearance({ [field]: value });
  };

  const handleDashboardWidgetChange = (field, value) => {
    setCurrentAppearance((prev) => {
      const updated = {
        ...prev,
        dashboardWidgets: { ...prev.dashboardWidgets, [field]: value },
      };
      updateAppearance({ dashboardWidgets: updated.dashboardWidgets });
      return updated;
    });
  };
  
  const handleAddMember = () => {
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setMemberDraft({ email: '', role: 'member' });
    setDialogOpen(false);
  };

  const handleInviteMember = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token || !currentOrg.id) {
        showSnackbar(t('settings.snackbars.noOrganization'), 'error');
        return;
      }
      if (!memberDraft.email.trim()) {
        showSnackbar(t('settings.snackbars.emailRequired'), 'error');
        return;
      }
      await axios.post(
        `${API_BASE_URL}/organizations/${currentOrg.id}/add-member/`,
        { email: memberDraft.email.trim().toLowerCase(), role: memberDraft.role },
        getAuthHeaders(token),
      );
      await loadOrganizationData(token);
      handleCloseDialog();
      showSnackbar(t('settings.snackbars.addMemberSuccess'));
    } catch (error) {
      console.error('Error while adding team member:', error);
      const detail = error.response?.data?.detail || error.message;
      const message = detail === 'User not found.'
        ? t('settings.snackbars.addMemberNotFound')
        : t('settings.snackbars.addMemberError', { message: detail });
      showSnackbar(message, 'error');
    }
  };

  const handleEditMember = (member) => {
    setEditMemberDialog({ open: true, member, role: member.role_in_organization || member.role || 'member' });
  };

  const handleSaveEditMember = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token || !currentOrg.id || !editMemberDialog.member) return;
      await axios.post(
        `${API_BASE_URL}/organizations/${currentOrg.id}/update-member-role/`,
        { user_id: editMemberDialog.member.id, role: editMemberDialog.role },
        getAuthHeaders(token),
      );
      await loadOrganizationData(token);
      setEditMemberDialog({ open: false, member: null, role: 'member' });
      showSnackbar(t('settings.snackbars.memberRoleUpdated'));
    } catch (error) {
      showSnackbar(t('settings.snackbars.memberRoleUpdateError', { message: error.response?.data?.detail || error.message }), 'error');
    }
  };
  
  const handleDeleteMember = async (id) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token || !currentOrg.id) {
        showSnackbar(t('settings.snackbars.noOrganization'), 'error');
        return;
      }

      await axios.post(
        `${API_BASE_URL}/organizations/${currentOrg.id}/remove-member/`,
        { user_id: id },
        getAuthHeaders(token),
      );

      await loadOrganizationData(token);
      showSnackbar(t('settings.snackbars.removeMemberSuccess'));
    } catch (error) {
      console.error('Error while removing team member:', error);
      showSnackbar(t('settings.snackbars.removeMemberError', { message: error.response?.data?.detail || error.message }), 'error');
    }
  };
  
  const handleGenerateNewApiKey = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { showSnackbar(t('settings.snackbars.authRequired'), 'error'); return; }
      const response = await axios.post(
        `${OLYMP_API_BASE_URL}/olymp/settings/generate-api-key/`,
        {},
        getAuthHeaders(token),
      );
      const newKey = response.data.apiKey;
      setCurrentApi((prev) => ({ ...prev, apiKey: newKey }));
      showSnackbar(t('settings.snackbars.apiKeyGenerated'));
    } catch (error) {
      showSnackbar(
        t('settings.snackbars.apiKeyGenerateError', { message: error.response?.data?.detail || error.message }),
        'error',
      );
    }
  };

  const isValidIpOrCidr = (value) => {
    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/(\d|[1-2]\d|3[0-2]))?$/;
    const ipv6 = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(\/\d{1,3})?$/;
    return ipv4.test(value) || ipv6.test(value);
  };

  const handleAddIpAddress = () => {
    const normalizedIp = newIpAddress.trim();
    if (!normalizedIp) return;
    if (!isValidIpOrCidr(normalizedIp)) {
      showSnackbar(t('settings.snackbars.invalidIp'), 'error');
      return;
    }
    if (currentApi.ipWhitelist.includes(normalizedIp)) {
      showSnackbar(t('settings.snackbars.duplicateIp'), 'info');
      return;
    }
    setCurrentApi((prev) => ({
      ...prev,
      ipWhitelist: [...prev.ipWhitelist, normalizedIp],
    }));
    setNewIpAddress('');
  };

  const handleEndOtherSessions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { showSnackbar(t('settings.snackbars.authRequired'), 'error'); return; }
      await axios.post(`${API_BASE_URL}/auth/logout-all/`, {}, getAuthHeaders(token));
      showSnackbar(t('settings.snackbars.sessionEnded'));
    } catch (error) {
      if (error.response?.status === 404) {
        showSnackbar(t('settings.snackbars.sessionEnded'));
      } else {
        showSnackbar(
          t('settings.snackbars.sessionEndError', { message: error.response?.data?.detail || error.message }),
          'error',
        );
      }
    }
  };

  const handleTestWebhook = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { showSnackbar(t('settings.snackbars.authRequired'), 'error'); return; }
      const response = await axios.post(
        `${OLYMP_API_BASE_URL}/olymp/settings/test-webhook/`,
        {},
        getAuthHeaders(token),
      );
      showSnackbar(response.data?.detail || t('settings.snackbars.webhookTested'));
    } catch (error) {
      showSnackbar(
        t('settings.snackbars.webhookTestError', { message: error.response?.data?.detail || error.message }),
        'error',
      );
    }
  };

  const compressImage = (file, maxSize, quality) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = () => resolve(null);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showSnackbar(t('settings.snackbars.invalidImageType'), 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar(t('settings.snackbars.imageTooLarge'), 'error');
      return;
    }
    e.target.value = '';
    const dataUrl = await compressImage(file, 150, 0.85);
    if (!dataUrl) return;
    const updatedAppearance = { ...currentAppearance, avatarDataUrl: dataUrl };
    setCurrentAppearance(updatedAppearance);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      await axios.patch(
        `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
        { appearanceSettings: updatedAppearance },
        getAuthHeaders(token),
      );
      showSnackbar(t('settings.snackbars.avatarSaved'));
    } catch (err) {
      showSnackbar(t('settings.snackbars.avatarSaveError', { message: err.message }), 'error');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showSnackbar(t('settings.snackbars.invalidImageType'), 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar(t('settings.snackbars.imageTooLarge'), 'error');
      return;
    }
    e.target.value = '';
    const dataUrl = await compressImage(file, 200, 0.85);
    if (!dataUrl) return;
    const updatedAppearance = { ...currentAppearance, orgLogoDataUrl: dataUrl };
    setCurrentAppearance(updatedAppearance);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      await axios.patch(
        `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
        { appearanceSettings: updatedAppearance },
        getAuthHeaders(token),
      );
      showSnackbar(t('settings.snackbars.logoSaved'));
    } catch (err) {
      showSnackbar(t('settings.snackbars.logoSaveError', { message: err.message }), 'error');
    }
  };

  const loadActivityLogs = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setActivityLogsLoading(true);
    try {
      const response = await axios.get(
        `${OLYMP_API_BASE_URL}/olymp/settings/activity-logs/`,
        getAuthHeaders(token),
      );
      setActivityLogs(response.data?.results || []);
      setActivityLogsLoaded(true);
    } catch (err) {
      console.error('Failed to load activity logs:', err);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const handleTwoFaToggle = (newValue) => {
    if (newValue) {
      setTwoFaCode('');
      setTwoFaQrCode('');
      setTwoFaSecret('');
      setTwoFaDialogOpen(true);
      handleSetup2FA();
    } else {
      setTwoFaDisableDialogOpen(true);
    }
  };

  const handleSetup2FA = async () => {
    setTwoFaLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { setTwoFaDialogOpen(false); return; }
      const response = await axios.post(
        `${OLYMP_API_BASE_URL}/olymp/settings/setup-2fa/`,
        {},
        getAuthHeaders(token),
      );
      setTwoFaQrCode(response.data.qrCodeDataUrl || '');
      setTwoFaSecret(response.data.secret || '');
    } catch (err) {
      showSnackbar(
        t('settings.snackbars.twoFaSetupError', { message: err.response?.data?.detail || err.message }),
        'error',
      );
      setTwoFaDialogOpen(false);
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (twoFaCode.trim().length !== 6) {
      showSnackbar(t('settings.snackbars.twoFaCodeRequired'), 'error');
      return;
    }
    setTwoFaLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${OLYMP_API_BASE_URL}/olymp/settings/verify-2fa/`,
        { code: twoFaCode.trim() },
        getAuthHeaders(token),
      );
      setCurrentProfile((prev) => ({ ...prev, twoFactorEnabled: true }));
      setTwoFaDialogOpen(false);
      setActivityLogsLoaded(false);
      showSnackbar(t('settings.snackbars.twoFaEnabled'));
    } catch (err) {
      showSnackbar(
        t('settings.snackbars.twoFaVerifyError', { message: err.response?.data?.detail || err.message }),
        'error',
      );
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setTwoFaLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${OLYMP_API_BASE_URL}/olymp/settings/disable-2fa/`,
        {},
        getAuthHeaders(token),
      );
      setCurrentProfile((prev) => ({ ...prev, twoFactorEnabled: false }));
      setTwoFaDisableDialogOpen(false);
      setActivityLogsLoaded(false);
      showSnackbar(t('settings.snackbars.twoFaDisabled'));
    } catch (err) {
      showSnackbar(
        t('settings.snackbars.twoFaDisableError', { message: err.response?.data?.detail || err.message }),
        'error',
      );
    } finally {
      setTwoFaLoading(false);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          {t('settings.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('settings.subtitle')}
        </Typography>
        
        {authStatus && !authStatus.connected && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.light' }}>
            <Typography variant="body2" color="text.primary">
              ⚠️ {t('settings.authWarningTitle')}: {authStatus.error || t('settings.authWarningFallback')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('settings.authWarningBody')}
            </Typography>
          </Paper>
        )}
        
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label={t('settings.tabAriaLabel')}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                py: 2
              }
            }}
          >
            <Tab 
              icon={<PersonIcon />} 
              label={t('settings.tabs.profile')} 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={<BusinessIcon />}
              label={t('settings.tabs.organization')}
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={<NotificationsIcon />}
              label={t('settings.tabs.notifications')}
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            {APP_NAME !== 'OLYMP' && (
              <Tab
                icon={<CreditCardIcon />}
                label={t('settings.tabs.billing')}
                iconPosition="start"
                sx={{ textTransform: 'none' }}
              />
            )}
            <Tab
              icon={<LanguageIcon />}
              label={t('settings.tabs.api')}
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={<PaletteIcon />}
              label={t('settings.tabs.appearance')}
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={<AdminPanelSettingsIcon />}
              label={t('settings.tabs.admin')}
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
          
          <Box sx={{ p: 3 }}>
            {loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                <CircularProgress color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {t('settings.loading')}
                </Typography>
              </Box>
            )}

            {/* Profil-Einstellungen */}
            {!loading && activeTab === TAB_INDICES.PROFILE && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <input
                        type="file"
                        accept="image/*"
                        ref={avatarInputRef}
                        style={{ display: 'none' }}
                        onChange={handleAvatarUpload}
                      />
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <Tooltip title={t('settings.sections.profile.uploadAvatar')}>
                            <IconButton
                              size="small"
                              onClick={() => avatarInputRef.current?.click()}
                              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <Avatar
                          src={currentAppearance?.avatarDataUrl || undefined}
                          sx={{
                            width: 120,
                            height: 120,
                            mb: 2,
                            fontSize: '3rem',
                            bgcolor: 'primary.main'
                          }}
                        >
                          {!currentAppearance?.avatarDataUrl && currentProfile.name.charAt(0)}
                        </Avatar>
                      </Badge>
                      <Typography variant="h6" gutterBottom>
                        {currentProfile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {currentProfile.role}
                      </Typography>
                      <Chip 
                        label={currentProfile.company} 
                        color="primary" 
                        variant="outlined" 
                        size="small" 
                        sx={{ mt: 1 }}
                      />
                      
                      <Divider sx={{ width: '100%', my: 2 }} />
                      
                      <List sx={{ width: '100%' }}>
                        <ListItem disableGutters>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <EmailIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={t('settings.sections.profile.summary.email')} 
                            secondary={currentProfile.email} 
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <PhoneIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={t('settings.sections.profile.summary.phone')} 
                            secondary={currentProfile.phone} 
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <LocationOnIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={t('settings.sections.profile.summary.address')}
                            secondary={currentProfile.address}
                          />
                        </ListItem>
                      </List>

                      {currentOrg.id && (
                        <>
                          <Divider sx={{ width: '100%', my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom sx={{ alignSelf: 'flex-start', fontWeight: 600 }}>
                            {t('settings.sections.profile.orgContext.title')}
                          </Typography>
                          <List sx={{ width: '100%' }}>
                            <ListItem disableGutters>
                              <ListItemText
                                primary={t('settings.sections.profile.orgContext.company')}
                                secondary={currentOrg.name || t('settings.sections.profile.orgContext.noInfo')}
                              />
                            </ListItem>
                            <ListItem disableGutters>
                              <ListItemText
                                primary={t('settings.sections.profile.orgContext.role')}
                                secondary={getMemberRoleLabel(currentUserOrgRole) || t('settings.sections.profile.orgContext.noInfo')}
                              />
                            </ListItem>
                          </List>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.profile.personalInformation')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('settings.sections.profile.fields.name')}
                            value={currentProfile.name}
                            onChange={(e) => handleProfileChange('name', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('settings.sections.profile.fields.email')}
                            value={currentProfile.email}
                            variant="outlined"
                            margin="normal"
                            type="email"
                            disabled
                            helperText={t('settings.sections.profile.helpers.emailManaged')}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('settings.sections.profile.fields.phone')}
                            value={currentProfile.phone}
                            onChange={(e) => handleProfileChange('phone', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('settings.sections.profile.fields.role')}
                            value={currentProfile.role}
                            onChange={(e) => handleProfileChange('role', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t('settings.sections.profile.fields.company')}
                            value={currentProfile.company}
                            onChange={(e) => handleProfileChange('company', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={t('settings.sections.profile.fields.address')}
                            value={currentProfile.address}
                            onChange={(e) => handleProfileChange('address', e.target.value)}
                            variant="outlined"
                            margin="normal"
                            multiline
                            rows={2}
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          {t('settings.sections.profile.securitySettings')}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Typography variant="subtitle2" gutterBottom>
                          {t('settings.sections.security.twoFactorTitle')}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={currentProfile.twoFactorEnabled}
                              onChange={(e) => handleTwoFaToggle(e.target.checked)}
                              color="primary"
                              disabled={twoFaLoading}
                            />
                          }
                          label={t('settings.sections.profile.twoFactorLabel')}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                          {t('settings.sections.profile.helpers.twoFactor')}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          {t('settings.sections.profile.fields.changePassword')}
                        </Typography>
                        <TextField
                          fullWidth
                          label={t('settings.sections.security.currentPassword')}
                          type="password"
                          variant="outlined"
                          margin="dense"
                          value={passwordFields.oldPassword}
                          onChange={(e) => setPasswordFields((prev) => ({ ...prev, oldPassword: e.target.value }))}
                        />
                        <TextField
                          fullWidth
                          label={t('settings.sections.security.newPassword')}
                          type="password"
                          variant="outlined"
                          margin="dense"
                          value={passwordFields.newPassword}
                          onChange={(e) => setPasswordFields((prev) => ({ ...prev, newPassword: e.target.value }))}
                          error={passwordFields.newPassword.length > 0 && passwordFields.newPassword.length < 8}
                          helperText={passwordFields.newPassword.length > 0 && passwordFields.newPassword.length < 8 ? t('settings.snackbars.passwordTooShort') : ''}
                        />
                        <TextField
                          fullWidth
                          label={t('settings.sections.security.confirmPassword')}
                          type="password"
                          variant="outlined"
                          margin="dense"
                          value={passwordFields.newPasswordConfirm}
                          onChange={(e) => setPasswordFields((prev) => ({ ...prev, newPasswordConfirm: e.target.value }))}
                          error={passwordFields.newPasswordConfirm.length > 0 && passwordFields.newPassword !== passwordFields.newPasswordConfirm}
                          helperText={passwordFields.newPasswordConfirm.length > 0 && passwordFields.newPassword !== passwordFields.newPasswordConfirm ? t('settings.snackbars.passwordMismatch') : ''}
                        />

                        <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
                          {t('settings.sections.security.sessionManagement')}
                        </Typography>
                        <Button variant="outlined" color="error" size="small" sx={{ mt: 0.5 }} onClick={handleEndOtherSessions}>
                          {t('settings.buttons.endOtherSessions')}
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {t('settings.sections.security.sessionHelp')}
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => handleSaveSettings('privacy')}>
                            {t('settings.buttons.saveSettings')}
                          </Button>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSettings('profile')}
                        >
                          {t('settings.buttons.saveChanges')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Organisations-Einstellungen (Accordion) */}
            {!loading && activeTab === TAB_INDICES.ORGANIZATION && (
              <Box>
                {/* Company (read-only) */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
                      <Avatar
                        src={currentAppearance?.orgLogoDataUrl || undefined}
                        sx={{ width: 40, height: 40, bgcolor: currentOrg.primaryColor || 'primary.main', fontSize: '1rem' }}
                      >
                        {!currentAppearance?.orgLogoDataUrl && (currentOrg.name?.charAt(0) || <BusinessIcon />)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {currentOrg.name || t('settings.sections.organization.noOrgYet')}
                        </Typography>
                        {currentOrg.type && (
                          <Typography variant="caption" color="text.secondary">
                            {getOrganizationTypeLabel(currentOrg.type)}
                          </Typography>
                        )}
                      </Box>
                      <Chip label={t('settings.sections.organization.accordion.company')} size="small" variant="outlined" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
                      {t('settings.sections.organization.companyReadOnlyNote')}
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('settings.sections.organization.fields.name')} value={currentOrg.name || ''} variant="outlined" disabled size="small" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('settings.sections.organization.fields.type')} value={getOrganizationTypeLabel(currentOrg.type)} variant="outlined" disabled size="small" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('settings.sections.organization.fields.size')} value={getOrganizationSizeLabel(currentOrg.size)} variant="outlined" disabled size="small" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('settings.sections.organization.fields.industry')} value={currentOrg.industry || ''} variant="outlined" disabled size="small" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label={t('settings.sections.organization.fields.website')} value={currentOrg.website || ''} variant="outlined" disabled size="small" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('settings.sections.organization.fields.primaryColor')}
                          value={currentOrg.primaryColor || ''}
                          variant="outlined"
                          disabled
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: currentOrg.primaryColor || 'grey.300' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Departments */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {t('settings.sections.organization.accordion.departments')}
                      </Typography>
                      <Chip label={currentOrg.departments.length} size="small" variant="outlined" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {currentOrg.departments.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('settings.sections.organization.accordion.noDepartments')}
                        </Typography>
                        {isOrgAdmin && (
                          <Button variant="outlined" startIcon={<AddIcon />} size="small" sx={{ mt: 1 }}
                            onClick={() => setDeptDialog({ open: true, name: '', description: '', loading: false })}>
                            {t('settings.sections.organization.accordion.createDepartment')}
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <>
                        <List dense disablePadding>
                          {currentOrg.departments.map((dept) => (
                            <ListItem key={dept.id} divider secondaryAction={
                              isOrgAdmin ? (
                                <IconButton size="small" color="error"
                                  onClick={() => setDeleteItemDialog({ open: true, type: 'department', id: dept.id, name: dept.name })}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }>
                              <ListItemText
                                primary={dept.name}
                                secondary={dept.description || `${dept.team_count || 0} teams · ${dept.member_count || 0} members`}
                              />
                            </ListItem>
                          ))}
                        </List>
                        {isOrgAdmin && (
                          <Box sx={{ pt: 1 }}>
                            <Button variant="outlined" startIcon={<AddIcon />} size="small"
                              onClick={() => setDeptDialog({ open: true, name: '', description: '', loading: false })}>
                              {t('settings.sections.organization.accordion.createDepartment')}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Teams */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {t('settings.sections.organization.accordion.teams')}
                      </Typography>
                      <Chip label={currentOrg.teams.length} size="small" variant="outlined" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {currentOrg.teams.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('settings.sections.organization.accordion.noTeams')}
                        </Typography>
                        {isOrgAdmin && (
                          <Button variant="outlined" startIcon={<AddIcon />} size="small" sx={{ mt: 1 }}
                            onClick={() => setTeamDialog({ open: true, name: '', description: '', departmentId: '', loading: false })}>
                            {t('settings.sections.organization.accordion.createTeam')}
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <>
                        <List dense disablePadding>
                          {currentOrg.teams.map((team) => (
                            <ListItem key={team.id} divider secondaryAction={
                              isOrgAdmin ? (
                                <IconButton size="small" color="error"
                                  onClick={() => setDeleteItemDialog({ open: true, type: 'team', id: team.id, name: team.name })}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }>
                              <ListItemText
                                primary={team.name}
                                secondary={team.department_name ? `${team.department_name} · ${team.member_count || 0} members` : `${team.member_count || 0} members`}
                              />
                            </ListItem>
                          ))}
                        </List>
                        {isOrgAdmin && (
                          <Box sx={{ pt: 1 }}>
                            <Button variant="outlined" startIcon={<AddIcon />} size="small"
                              onClick={() => setTeamDialog({ open: true, name: '', description: '', departmentId: '', loading: false })}>
                              {t('settings.sections.organization.accordion.createTeam')}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Members */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {t('settings.sections.organization.accordion.members')}
                      </Typography>
                      <Chip label={currentOrg.members.length} size="small" variant="outlined" color="primary" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {!currentOrg.id ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('settings.sections.team.noOrg.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('settings.sections.team.noOrg.description')}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          {isOrgAdmin && (
                            <Button variant="outlined" startIcon={<AddIcon />} size="small" onClick={handleAddMember}>
                              {t('settings.buttons.inviteMember')}
                            </Button>
                          )}
                        </Box>
                        {currentOrg.members.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('settings.sections.team.noMembers')}
                            </Typography>
                          </Box>
                        ) : (
                          <List disablePadding>
                            {currentOrg.members.map((member) => {
                              const memberName = member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email;
                              const memberRole = member.role_in_organization || member.role || 'member';
                              const isCurrentUser = member.email === currentProfile.email;
                              return (
                                <ListItem
                                  key={member.id}
                                  secondaryAction={
                                    isOrgAdmin && !isCurrentUser ? (
                                      <Box>
                                        <Tooltip title={t('settings.sections.organization.actions.edit')}>
                                          <IconButton edge="end" sx={{ mr: 1 }} onClick={() => handleEditMember({ ...member, role: memberRole })}>
                                            <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('settings.sections.organization.actions.remove')}>
                                          <IconButton edge="end" onClick={() => setRemoveConfirmDialog({ open: true, memberId: member.id, memberName })}>
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    ) : isCurrentUser && !isOrgAdmin ? (
                                      <Tooltip title={t('settings.sections.organization.accordion.leaveTeam')}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          onClick={() => setRemoveConfirmDialog({ open: true, memberId: member.id, memberName })}
                                        >
                                          {t('settings.sections.organization.accordion.leaveTeam')}
                                        </Button>
                                      </Tooltip>
                                    ) : null
                                  }
                                >
                                  <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                                      {memberName.charAt(0).toUpperCase()}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography component="span" variant="body1">{memberName}</Typography>
                                        {isCurrentUser && (
                                          <Chip label={t('settings.sections.team.youLabel')} size="small" variant="outlined" color="primary" />
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <React.Fragment>
                                        <Typography component="span" variant="body2" color="text.primary">{member.email}</Typography>
                                        {' — '}
                                        <Chip
                                          label={getMemberRoleLabel(memberRole)}
                                          size="small"
                                          color={memberRole === 'admin' ? 'primary' : 'default'}
                                          variant={memberRole === 'admin' ? 'filled' : 'outlined'}
                                        />
                                      </React.Fragment>
                                    }
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}

            {/* Benachrichtigungs-Einstellungen */}
            {!loading && activeTab === TAB_INDICES.NOTIFICATIONS && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.notifications.emailTitle')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <List>
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.newRequests.title')}
                            secondary={t('settings.sections.notifications.items.newRequests.email')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.newRequests}
                              onChange={(e) => handleNotificationChange('email', 'newRequests', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.statusChanges.title')}
                            secondary={t('settings.sections.notifications.items.statusChanges.email')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.statusChanges}
                              onChange={(e) => handleNotificationChange('email', 'statusChanges', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.messages.title')}
                            secondary={t('settings.sections.notifications.items.messages.email')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.messages}
                              onChange={(e) => handleNotificationChange('email', 'messages', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.billing.title')}
                            secondary={t('settings.sections.notifications.items.billing.email')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.billing}
                              onChange={(e) => handleNotificationChange('email', 'billing', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.weeklyReport.title')}
                            secondary={t('settings.sections.notifications.items.weeklyReport.email')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.weeklyReport}
                              onChange={(e) => handleNotificationChange('email', 'weeklyReport', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.notifications.inAppTitle')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <List>
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.newRequests.title')}
                            secondary={t('settings.sections.notifications.items.newRequests.inApp')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.newRequests}
                              onChange={(e) => handleNotificationChange('inApp', 'newRequests', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.statusChanges.title')}
                            secondary={t('settings.sections.notifications.items.statusChanges.inApp')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.statusChanges}
                              onChange={(e) => handleNotificationChange('inApp', 'statusChanges', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.messages.title')}
                            secondary={t('settings.sections.notifications.items.messages.inApp')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.messages}
                              onChange={(e) => handleNotificationChange('inApp', 'messages', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary={t('settings.sections.notifications.items.billing.title')}
                            secondary={t('settings.sections.notifications.items.billing.inApp')}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.billing}
                              onChange={(e) => handleNotificationChange('inApp', 'billing', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      </List>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSettings('notifications')}
                        >
                          {t('settings.buttons.saveSettings')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* Admin Panel (Privacy & Security + Activity Logs) */}
            {!loading && activeTab === TAB_INDICES.ADMIN && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.privacy.privacyTitle')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('settings.sections.privacy.dataSharingTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {t('settings.sections.privacy.dataSharingDescription')}
                        </Typography>
                        <Select
                          value={currentPrivacy.dataSharing}
                          onChange={(e) => handlePrivacyChange('dataSharing', e.target.value)}
                          fullWidth
                        >
                          {dataSharingOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {t(`settings.sections.privacy.dataSharingOptions.${option}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={currentPrivacy.anonymizeResults}
                            onChange={(e) => handlePrivacyChange('anonymizeResults', e.target.checked)}
                            color="primary"
                          />
                        }
                        label={t('settings.sections.privacy.anonymizeLabel')}
                        sx={{ display: 'block', mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t('settings.sections.privacy.anonymizeDescription')}
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={currentPrivacy.allowAnalytics}
                            onChange={(e) => handlePrivacyChange('allowAnalytics', e.target.checked)}
                            color="primary"
                          />
                        }
                        label={t('settings.sections.privacy.analyticsLabel')}
                        sx={{ display: 'block', mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t('settings.sections.privacy.analyticsDescription')}
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        {t('settings.sections.privacy.retentionTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('settings.sections.privacy.retentionDescription')}
                      </Typography>
                      <Box sx={{ width: '100%', px: 2 }}>
                        <Slider
                          value={currentPrivacy.retentionPeriod}
                          onChange={(e, newValue) => handlePrivacyChange('retentionPeriod', newValue)}
                          aria-labelledby="retention-period-slider"
                          valueLabelDisplay="auto"
                          step={6}
                          marks={retentionMarks}
                          min={6}
                          max={60}
                          valueLabelFormat={formatRetentionValue}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
                        <Typography variant="h6" gutterBottom>
                          {t('settings.sections.security.accessLogsTitle')}
                        </Typography>
                        <Tooltip title={t('settings.sections.security.refreshLogs')}>
                          <IconButton size="small" onClick={() => { setActivityLogsLoaded(false); loadActivityLogs(); }} disabled={activityLogsLoading}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <Typography variant="body2" paragraph>
                        {t('settings.sections.security.accessLogsDescription')}
                      </Typography>

                      {activityLogsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : activityLogs.length === 0 ? (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                          <SecurityIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.sections.security.accessLogsEmpty')}
                          </Typography>
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>{t('settings.sections.security.accessLogCols.event')}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t('settings.sections.security.accessLogCols.description')}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t('settings.sections.security.accessLogCols.ip')}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t('settings.sections.security.accessLogCols.time')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {activityLogs.map((log) => (
                                <TableRow key={log.id} hover>
                                  <TableCell>
                                    <Chip
                                      label={log.eventType.replace(/_/g, ' ')}
                                      size="small"
                                      variant="outlined"
                                      color={log.eventType.includes('2fa') ? 'warning' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell>{log.description}</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{log.ipAddress || '—'}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                                    {new Date(log.createdAt).toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveSettings('privacy')}
                    >
                      {t('settings.buttons.saveSettings')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Abrechnungs-Einstellungen */}
            {!loading && showBillingTab && activeTab === TAB_INDICES.BILLING && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.billing.currentPlan')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Chip 
                          label={currentBilling.plan} 
                          color="primary" 
                          sx={{ fontSize: '1.2rem', py: 3, px: 2 }}
                        />
                      </Box>
                      
                      <Typography variant="body1" gutterBottom>
                        <strong>{t('settings.sections.billing.nextBilling')}</strong> {currentBilling.nextBilling}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        <strong>{t('settings.sections.billing.paymentMethod')}</strong> {currentBilling.paymentMethod}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        <strong>{t('settings.sections.billing.institutionNumber')}</strong> {currentBilling.accountNumber}
                      </Typography>
                      
                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          sx={{ mb: 2 }}
                        >
                          {t('settings.buttons.changeBillingDetails')}
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                        >
                          {t('settings.buttons.changeServicePackage')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.billing.invoiceHistory')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('settings.sections.billing.invoiceId')}</TableCell>
                              <TableCell>{t('settings.sections.billing.date')}</TableCell>
                              <TableCell>{t('settings.sections.billing.amount')}</TableCell>
                              <TableCell>{t('settings.sections.billing.status')}</TableCell>
                              <TableCell>{t('settings.sections.billing.actions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentBilling.invoices.map((invoice) => (
                              <TableRow key={invoice.id}>
                                <TableCell>{invoice.id}</TableCell>
                                <TableCell>{invoice.date}</TableCell>
                                <TableCell>{invoice.amount}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={getInvoiceStatusLabel(invoice.status)} 
                                    color={invoice.status === 'bezahlt' ? 'success' : 'warning'} 
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                  >
                                    {t('settings.buttons.pdf')}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          {t('settings.sections.billing.billingAddress')}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label={t('settings.sections.profile.fields.company')}
                              value={currentOrg.name}
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label={t('settings.sections.billing.contactPerson')}
                              value={currentProfile.name}
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label={t('settings.sections.profile.fields.address')}
                              value={currentProfile.address}
                              variant="outlined"
                              margin="normal"
                              multiline
                              rows={2}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label={t('settings.sections.billing.invoiceEmail')}
                              value={currentProfile.email}
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label={t('settings.sections.billing.vatId')}
                              placeholder="DE123456789"
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                        </Grid>
                        
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSaveSettings('billing')}
                          >
                            {t('settings.buttons.saveChanges')}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* API & Integrationen */}
            {!loading && activeTab === TAB_INDICES.API_INTEGRATIONS && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.api.settingsTitle')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={currentApi.enabled}
                            onChange={(e) => handleApiChange('enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label={t('settings.sections.api.enableAccess')}
                        sx={{ display: 'block', mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t('settings.sections.api.enableAccessDescription')}
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        {t('settings.sections.api.apiKey')}
                      </Typography>
                      <TextField
                        fullWidth
                        value={currentApi.apiKey}
                        variant="outlined"
                        margin="normal"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              {currentApi.apiKey && (
                                <Tooltip title={t('settings.sections.api.copyKey')}>
                                  <IconButton
                                    onClick={() => {
                                      navigator.clipboard.writeText(currentApi.apiKey);
                                      showSnackbar(t('settings.snackbars.apiKeyCopied'));
                                    }}
                                    edge="end"
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <IconButton
                                aria-label={t('settings.sections.api.toggleKeyVisibility')}
                                onClick={() => setShowApiKey(!showApiKey)}
                                edge="end"
                              >
                                {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                          type: showApiKey ? 'text' : 'password'
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={handleGenerateNewApiKey}
                        sx={{ mt: 1 }}
                      >
                        {t('settings.buttons.generateNewKey')}
                      </Button>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('settings.sections.api.newKeyWarning')}
                      </Typography>
                      
                      <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
                        {t('settings.sections.api.rateLimit')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('settings.sections.api.rateLimitDescription')}
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={currentApi.rateLimit}
                        onChange={(e) => handleApiChange('rateLimit', parseInt(e.target.value))}
                        variant="outlined"
                        margin="normal"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{t('settings.sections.api.requestsPerHour')}</InputAdornment>,
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.api.webhookTitle')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="body2" paragraph>
                        {t('settings.sections.api.webhookDescription')}
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label={t('settings.sections.api.webhookUrl')}
                        value={currentApi.webhookUrl}
                        onChange={(e) => handleApiChange('webhookUrl', e.target.value)}
                        variant="outlined"
                        margin="normal"
                        placeholder="https://example.com/webhook"
                      />
                      
                      <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
                        {t('settings.sections.api.webhookEvents')}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentApi.webhookEvents.analysisResults}
                            onChange={(e) =>
                              handleApiWebhookEventChange('analysisResults', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.api.analysisResults')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentApi.webhookEvents.statusChanges}
                            onChange={(e) =>
                              handleApiWebhookEventChange('statusChanges', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.api.statusChanges')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentApi.webhookEvents.messages}
                            onChange={(e) =>
                              handleApiWebhookEventChange('messages', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.api.messages')}
                      />
                      
                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{ mr: 1 }}
                          onClick={handleTestWebhook}
                        >
                          {t('settings.buttons.testWebhook')}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSettings('webhook')}
                        >
                          {t('settings.buttons.saveWebhook')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.api.ipWhitelist')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="body2" paragraph>
                        {t('settings.sections.api.ipWhitelistDescription')}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        {currentApi.ipWhitelist.map((ip, index) => (
                          <Chip
                            key={index}
                            label={ip}
                            onDelete={() => {
                              const newList = [...currentApi.ipWhitelist];
                              newList.splice(index, 1);
                              handleApiChange('ipWhitelist', newList);
                            }}
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label={t('settings.sections.api.newIpAddress')}
                          variant="outlined"
                          size="small"
                          placeholder={t('settings.sections.api.newIpPlaceholder')}
                          fullWidth
                          value={newIpAddress}
                          onChange={(e) => setNewIpAddress(e.target.value)}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAddIpAddress}
                        >
                          {t('settings.buttons.add')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.api.integrations')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>R</Avatar>
                                <Typography variant="h6">
                                  R Studio
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {t('settings.sections.api.integrationsCards.rStudio')}
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                              >
                                {t('settings.buttons.connect')}
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>P</Avatar>
                                <Typography variant="h6">
                                  Python
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {t('settings.sections.api.integrationsCards.python')}
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                              >
                                {t('settings.buttons.connect')}
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>T</Avatar>
                                <Typography variant="h6">
                                  Tableau
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {t('settings.sections.api.integrationsCards.tableau')}
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                              >
                                {t('settings.buttons.connect')}
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveSettings('api')}
                    >
                      {t('settings.buttons.saveAllApi')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Darstellungs-Einstellungen */}
            {!loading && activeTab === TAB_INDICES.APPEARANCE && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.appearance.title')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="subtitle2" gutterBottom>
                        {t('settings.sections.appearance.theme')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Button
                          variant={currentAppearance.theme === 'light' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleAppearanceChange('theme', 'light')}
                          sx={{ flex: 1 }}
                        >
                          {t('settings.sections.appearance.themes.light')}
                        </Button>
                        <Button
                          variant={currentAppearance.theme === 'dark' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleAppearanceChange('theme', 'dark')}
                          sx={{ flex: 1 }}
                        >
                          {t('settings.sections.appearance.themes.dark')}
                        </Button>
                        <Button
                          variant={currentAppearance.theme === 'system' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleAppearanceChange('theme', 'system')}
                          sx={{ flex: 1 }}
                        >
                          {t('settings.sections.appearance.themes.system')}
                        </Button>
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        {t('settings.sections.appearance.primaryColor')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                        {['#FF7A00', '#00B8D4', '#4CAF50', '#F44336', '#9C27B0', '#2196F3'].map((color) => (
                          <Box
                            key={color}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: color,
                              cursor: 'pointer',
                              border: currentAppearance.primaryColor === color ? '2px solid #000' : 'none',
                              '&:hover': {
                                opacity: 0.8
                              }
                            }}
                            onClick={() => handleAppearanceChange('primaryColor', color)}
                          />
                        ))}
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        {t('settings.sections.appearance.fontSize')}
                      </Typography>
                      <Box sx={{ width: '100%', px: 2, mb: 3 }}>
                        <Slider
                          value={currentAppearance.fontScale}
                          onChange={(e, newValue) => handleAppearanceChange('fontScale', newValue)}
                          step={0.1}
                          marks={fontScaleMarks}
                          min={0.8}
                          max={1.2}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                        />
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        {t('settings.sections.appearance.animations')}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentAppearance.animationsEnabled}
                            onChange={(e) =>
                              handleAppearanceChange('animationsEnabled', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.appearance.animationsEnabled')}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.appearance.languageRegion')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{t('settings.sections.appearance.switchLanguage')}</InputLabel>
                        <Select
                          value={normalizeLanguage(currentProfile.language || normalizedLanguage)}
                          label={t('settings.sections.appearance.switchLanguage')}
                          onChange={(e) => handleLanguageChange(e.target.value)}
                        >
                          {SUPPORTED_LANGUAGES.map((option) => (
                            <MenuItem key={option.code} value={option.code}>
                              {`${option.flag} ${option.code.toUpperCase()} - ${t(`common.language.${option.code}`)}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{t('settings.sections.appearance.dateFormat')}</InputLabel>
                        <Select
                          value={currentAppearance.dateFormat}
                          label={t('settings.sections.appearance.dateFormat')}
                          onChange={(e) => handleAppearanceChange('dateFormat', e.target.value)}
                        >
                          <MenuItem value="dd.mm.yyyy">DD.MM.YYYY</MenuItem>
                          <MenuItem value="mm/dd/yyyy">MM/DD/YYYY</MenuItem>
                          <MenuItem value="yyyy-mm-dd">YYYY-MM-DD</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{t('settings.sections.appearance.timeZone')}</InputLabel>
                        <Select
                          value={currentAppearance.timezone}
                          label={t('settings.sections.appearance.timeZone')}
                          onChange={(e) => handleAppearanceChange('timezone', e.target.value)}
                        >
                          {timeZoneOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(`settings.sections.appearance.timeZones.${option.key}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('settings.sections.appearance.dashboard')}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="subtitle2" gutterBottom>
                        {t('settings.sections.appearance.defaultHomepage')}
                      </Typography>
                      <FormControl fullWidth margin="normal">
                        <Select
                          value={currentAppearance.defaultHomepage}
                          onChange={(e) =>
                            handleAppearanceChange('defaultHomepage', e.target.value)
                          }
                        >
                          {dashboardHomepageOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {t(`settings.sections.appearance.homepageOptions.${option}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                        {t('settings.sections.appearance.widgets.title')}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentAppearance.dashboardWidgets.incomingRequests}
                            onChange={(e) =>
                              handleDashboardWidgetChange('incomingRequests', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.appearance.widgets.incomingRequests')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentAppearance.dashboardWidgets.statistics}
                            onChange={(e) =>
                              handleDashboardWidgetChange('statistics', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.appearance.widgets.statistics')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentAppearance.dashboardWidgets.runningAnalyses}
                            onChange={(e) =>
                              handleDashboardWidgetChange('runningAnalyses', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.appearance.widgets.runningAnalyses')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentAppearance.dashboardWidgets.paymentOverview}
                            onChange={(e) =>
                              handleDashboardWidgetChange('paymentOverview', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.appearance.widgets.paymentOverview')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentAppearance.dashboardWidgets.activityLog}
                            onChange={(e) =>
                              handleDashboardWidgetChange('activityLog', e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={t('settings.sections.appearance.widgets.activityLog')}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveSettings('appearance')}
                    >
                      {t('settings.buttons.saveSettings')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Paper>
      </Box>
      
      {/* Dialog zum Hinzufügen eines neuen Teammitglieds */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('settings.sections.organization.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('settings.sections.organization.dialog.description')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('settings.sections.organization.dialog.email')}
            type="email"
            fullWidth
            variant="outlined"
            value={memberDraft.email}
            onChange={(e) => setMemberDraft((prev) => ({ ...prev, email: e.target.value }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('settings.sections.organization.dialog.role')}</InputLabel>
            <Select
              value={memberDraft.role}
              label={t('settings.sections.organization.dialog.role')}
              onChange={(e) => setMemberDraft((prev) => ({ ...prev, role: e.target.value }))}
            >
              {memberRoleOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`settings.sections.organization.roles.${option}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('settings.buttons.cancel')}</Button>
          <Button onClick={handleInviteMember} variant="contained" color="primary" disabled={!memberDraft.email.trim()}>
            {t('settings.buttons.invite')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog zum Bearbeiten der Mitgliederrolle */}
      <Dialog open={editMemberDialog.open} onClose={() => setEditMemberDialog({ open: false, member: null, role: 'member' })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('settings.sections.team.editRoleDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('settings.sections.team.editRoleDialog.description', { email: editMemberDialog.member?.email || '' })}
          </DialogContentText>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('settings.sections.organization.dialog.role')}</InputLabel>
            <Select
              value={editMemberDialog.role}
              label={t('settings.sections.organization.dialog.role')}
              onChange={(e) => setEditMemberDialog((prev) => ({ ...prev, role: e.target.value }))}
            >
              {memberRoleOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`settings.sections.organization.roles.${option}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMemberDialog({ open: false, member: null, role: 'member' })}>{t('settings.buttons.cancel')}</Button>
          <Button onClick={handleSaveEditMember} variant="contained" color="primary">
            {t('settings.buttons.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Remove member confirmation dialog */}
      <Dialog open={removeConfirmDialog.open} onClose={() => setRemoveConfirmDialog({ open: false, memberId: null, memberName: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('settings.sections.team.removeConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.sections.team.removeConfirm.description', { name: removeConfirmDialog.memberName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmDialog({ open: false, memberId: null, memberName: '' })}>
            {t('settings.buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const idToRemove = removeConfirmDialog.memberId;
              setRemoveConfirmDialog({ open: false, memberId: null, memberName: '' });
              handleDeleteMember(idToRemove);
            }}
          >
            {t('settings.sections.team.removeConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog
        open={twoFaDialogOpen}
        onClose={() => { if (!twoFaLoading) setTwoFaDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('settings.sections.twoFa.setupTitle')}</DialogTitle>
        <DialogContent>
          {twoFaLoading && !twoFaQrCode ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {t('settings.sections.twoFa.setupDescription')}
              </DialogContentText>
              {twoFaQrCode && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                  <img src={twoFaQrCode} alt="QR Code" style={{ width: 200, height: 200, border: '1px solid #eee', borderRadius: 4 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
                    {t('settings.sections.twoFa.manualEntryLabel')}
                  </Typography>
                  <Tooltip title={t('settings.sections.twoFa.clickToCopy')}>
                    <Typography
                      variant="body2"
                      onClick={() => { navigator.clipboard.writeText(twoFaSecret); showSnackbar(t('settings.sections.twoFa.secretCopied')); }}
                      sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1, mt: 0.5, cursor: 'pointer', letterSpacing: 1 }}
                    >
                      {twoFaSecret}
                    </Typography>
                  </Tooltip>
                </Box>
              )}
              <TextField
                label={t('settings.sections.twoFa.codeLabel')}
                value={twoFaCode}
                onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                placeholder="000000"
                disabled={twoFaLoading}
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFaDialogOpen(false)} disabled={twoFaLoading}>
            {t('settings.buttons.cancel')}
          </Button>
          <Button
            onClick={handleVerify2FA}
            variant="contained"
            disabled={twoFaLoading || twoFaCode.length !== 6}
          >
            {twoFaLoading ? <CircularProgress size={20} /> : t('settings.sections.twoFa.verifyButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2FA Disable Confirmation Dialog */}
      <Dialog
        open={twoFaDisableDialogOpen}
        onClose={() => { if (!twoFaLoading) setTwoFaDisableDialogOpen(false); }}
      >
        <DialogTitle>{t('settings.sections.twoFa.disableTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.sections.twoFa.disableDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFaDisableDialogOpen(false)} disabled={twoFaLoading}>
            {t('settings.buttons.cancel')}
          </Button>
          <Button onClick={handleDisable2FA} color="error" variant="contained" disabled={twoFaLoading}>
            {twoFaLoading ? <CircularProgress size={20} /> : t('settings.sections.twoFa.disableButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Department Dialog */}
      <Dialog open={deptDialog.open} onClose={() => setDeptDialog({ open: false, name: '', description: '', loading: false })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('settings.sections.organization.accordion.createDepartment')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Name" value={deptDialog.name} sx={{ mt: 1, mb: 2 }}
            onChange={(e) => setDeptDialog((d) => ({ ...d, name: e.target.value }))}
          />
          <TextField
            fullWidth multiline rows={2} label="Description (optional)" value={deptDialog.description}
            onChange={(e) => setDeptDialog((d) => ({ ...d, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeptDialog({ open: false, name: '', description: '', loading: false })}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="contained" onClick={handleCreateDepartment} disabled={!deptDialog.name.trim() || deptDialog.loading}>
            {deptDialog.loading ? <CircularProgress size={16} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={teamDialog.open} onClose={() => setTeamDialog({ open: false, name: '', description: '', departmentId: '', loading: false })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('settings.sections.organization.accordion.createTeam')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Name" value={teamDialog.name} sx={{ mt: 1, mb: 2 }}
            onChange={(e) => setTeamDialog((d) => ({ ...d, name: e.target.value }))}
          />
          {currentOrg.departments.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Department (optional)</InputLabel>
              <Select value={teamDialog.departmentId} label="Department (optional)"
                onChange={(e) => setTeamDialog((d) => ({ ...d, departmentId: e.target.value }))}>
                <MenuItem value=""><em>None</em></MenuItem>
                {currentOrg.departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth multiline rows={2} label="Description (optional)" value={teamDialog.description}
            onChange={(e) => setTeamDialog((d) => ({ ...d, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialog({ open: false, name: '', description: '', departmentId: '', loading: false })}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="contained" onClick={handleCreateTeam} disabled={!teamDialog.name.trim() || teamDialog.loading}>
            {teamDialog.loading ? <CircularProgress size={16} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Department / Team Confirmation */}
      <Dialog open={deleteItemDialog.open} onClose={() => setDeleteItemDialog({ open: false, type: '', id: null, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {deleteItemDialog.type === 'department' ? 'Department' : 'Team'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteItemDialog.name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteItemDialog({ open: false, type: '', id: null, name: '' })}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteDeptOrTeam}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Erfolgsmeldungen */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default SettingsPage;
